import { css, cva, sva } from "@stubhub/broadway-foundation/css";

export const containerStyle = css({
  maxWidth: "[1200px]",
  boxSizing: "content-box",
  marginInlineStart: "auto",
  marginInlineEnd: "auto",
  transitionProperty: "[padding]",
  transitionDuration: "change.default",
});

export const CALENDAR_CSS_VARIABLES = {
  primaryColor: "--component-primary-color",
} as const;

export const bottomButtonContainer = css({
  display: "flex",
  justifyContent: "center",
  paddingBlock: "primitives.200",
});

export const fullWidthButton = css({
  width: "full",
});

export const seeMoreContainer = css({
  display: "flex",
  justifyContent: "center",
});

export const seeMoreButton = css({
  minWidth: "[200px]",
});

const calendarHeaderBase = {
  backgroundColor: "bg.surface",
  textAlign: "center",
  marginBlockStart: "primitives.250",
  marginBlockEnd: "primitives.400",
  paddingBlock: "primitives.200",
  borderBlockStartWidth: "primitives.50",
  borderBlockEndWidth: "primitives.50",
  borderBlockStartStyle: "solid",
  borderBlockEndStyle: "solid",
  borderInlineStartStyle: "solid",
  borderInlineEndStyle: "solid",
  borderBlockStartColor: "border",
  borderBlockEndColor: "border",
  borderInlineStartColor: "border",
  borderInlineEndColor: "border",
  color: "text.subtle",
} as const;

export const calendarHeaderContainer = css({
  ...calendarHeaderBase,
  position: "sticky",
  insetBlockStart: "[68px]",
  zIndex: "[1]",
});

export const calendarHeaderContainerStatic = css({
  ...calendarHeaderBase,
  marginBlockStart: "primitives.0",
});

/**
 * Recipe combining containerStyle + calendarHeadingWeekdays for the weekday
 * header row. Replaces `cx(containerStyle, calendarHeadingWeekdays)`.
 */
export const calendarWeekdayContainer = cva({
  base: {
    maxWidth: "[1200px]",
    boxSizing: "content-box",
    marginInlineStart: "auto",
    marginInlineEnd: "auto",
    transitionProperty: "[padding]",
    transitionDuration: "change.default",
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    columnGap: "primitives.100",
    rowGap: "primitives.100",
  },
});

export const calendarNavigationHeading = css({
  marginBlockEnd: "primitives.200",
  color: "text.subtle",
});

export const calendarMonth = css({
  position: "relative",
  "&:not(:last-child)": {
    marginBlockEnd: "primitives.600",
  },
});

/**
 * Recipe for the calendar body grid.
 * Replaces `cx(calendarBody, calendarBodyLoading?)` and
 * `cx(calendarBody, calendarBodySkeleton)`.
 */
export const calendarBodyRecipe = cva({
  base: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    columnGap: "primitives.100",
    rowGap: "primitives.100",
    alignItems: "stretch",
  },
  variants: {
    loading: {
      loading: {
        opacity: 0.3,
        pointerEvents: "none",
        userSelect: "none",
      },
      idle: {},
    },
    skeleton: {
      skeleton: {
        maskImage: "linear-gradient(to bottom, black 0%, transparent 100%)",
      },
      content: {},
    },
  },
  defaultVariants: { loading: "idle", skeleton: "content" },
});

export const calendarBodyOverlay = css({
  position: "absolute",
  insetBlockStart: "primitives.0",
  insetInlineStart: "primitives.0",
  width: "full",
  height: "full",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const calendarBodyOverlayContent = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  columnGap: "primitives.100",
  rowGap: "primitives.100",
  paddingBlock: "primitives.200",
  paddingInline: "primitives.200",
  backgroundColor: "bg.surface",
  borderStartStartRadius: "element.default",
  borderStartEndRadius: "element.default",
  borderEndStartRadius: "element.default",
  borderEndEndRadius: "element.default",
  boxShadow: "floating",
});

export const mobileCalendarCard = css({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "stretch",
  columnGap: "primitives.100",
  rowGap: "primitives.100",
});

export const mobileCalendarBody = css({
  paddingBlockStart: "primitives.200",
});

export const defaultContainerContentFallback = css({
  paddingBlock: "primitives.100",
  paddingInline: "primitives.100",
});

/**
 * Slot recipe for one full date card. The `root` slot is the outer tile
 * (named `root` rather than `container` to avoid Panda's eslint plugin
 * misreading the slot key as the CSS `container` shorthand inside
 * `compoundVariants`).
 *
 * Variants:
 * - `mode` — `interactive` (anchor/button), `static` (multi-event parent <div>),
 *   or `empty` (no event for that day).
 * - `primaryColorState` — `"applied"` or `"none"`: whether `--component-primary-color`
 *   is set on the tile. Drives top-line border/text colors and the today-highlight tint.
 * - `minHeight` — `"clamped"` or `"auto"`: clamps the tile to a minimum height (used by skeletons).
 * - `todayHighlight` — `"today"` or `"default"`: emphasises the day-of-month chip when the tile represents today.
 * - `dayOfMonthState` — `normal` or `tba` (wider chip when day is to-be-announced).
 * - `headingBorder` — `"borderless"` or `"bordered"`: drops the top-line border for callers that don't want
 *   the heading divider (e.g. multi-event days where portions own the border).
 */
export const calendarTile = sva({
  slots: ["root", "topLine", "body", "cta", "dayOfMonth"],
  className: "calendarTile",
  base: {
    root: {
      display: "flex",
      flexDirection: "column",
      flexWrap: "nowrap",
      height: "full",
      backgroundColor: "bg.surface",
      paddingBlock: "primitives.0",
      paddingInline: "primitives.0",
      columnGap: "primitives.0",
      rowGap: "primitives.0",
      borderBlockStartWidth: "primitives.25",
      borderBlockEndWidth: "primitives.25",
      borderInlineStartWidth: "primitives.25",
      borderInlineEndWidth: "primitives.25",
      borderBlockStartStyle: "solid",
      borderBlockEndStyle: "solid",
      borderInlineStartStyle: "solid",
      borderInlineEndStyle: "solid",
      borderBlockStartColor: "border",
      borderBlockEndColor: "border",
      borderInlineStartColor: "border",
      borderInlineEndColor: "border",
      borderStartStartRadius: "container",
      borderStartEndRadius: "container",
      borderEndStartRadius: "container",
      borderEndEndRadius: "container",
      textAlign: "center",
      overflowX: "hidden",
      overflowY: "hidden",
      cursor: "pointer",
      transitionProperty: "[all]",
      transitionDuration: "[150ms]",
      transitionTimingFunction: "interaction.press",
      _hover: {
        backgroundColor: "bg.surface",
        borderBlockStartColor: "var(--component-primary-color)",
        borderBlockEndColor: "var(--component-primary-color)",
        borderInlineStartColor: "var(--component-primary-color)",
        borderInlineEndColor: "var(--component-primary-color)",
      },
      _active: { backgroundColor: "bg.surface" },
    },
    topLine: {
      display: "flex",
      width: "full",
      paddingBlock: "primitives.50",
      paddingInline: "primitives.100",
      justifyContent: "space-between",
      alignItems: "center",
      columnGap: "primitives.50",
      rowGap: "primitives.50",
      textAlign: "start",
      backgroundColor: "var(--component-primary-color)",
      borderBlockStartStyle: "solid",
      borderBlockEndStyle: "solid",
      borderInlineStartStyle: "solid",
      borderInlineEndStyle: "solid",
      borderBlockEndWidth: "primitives.25",
    },
    body: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      justifyContent: "center",
      width: "full",
    },
    cta: {
      display: "flex",
      width: "full",
      alignItems: "center",
      justifyContent: "center",
      columnGap: "primitives.50",
      rowGap: "primitives.50",
      paddingBlock: "primitives.100",
      paddingInline: "primitives.200",
      borderBlockStartStyle: "solid",
      borderBlockEndStyle: "solid",
      borderInlineStartStyle: "solid",
      borderInlineEndStyle: "solid",
      borderBlockStartColor: "border",
      borderBlockEndColor: "border",
      borderInlineStartColor: "border",
      borderInlineEndColor: "border",
      borderBlockStartWidth: "primitives.25",
    },
    dayOfMonth: {
      display: "flex",
      justifyContent: "center",
      width: "[calc(({spacing.primitives.25} * 2) + {lineHeights.primitives.500})]",
      paddingBlock: "primitives.25",
      paddingInline: "primitives.25",
      borderStartStartRadius: "full",
      borderStartEndRadius: "full",
      borderEndStartRadius: "full",
      borderEndEndRadius: "full",
    },
  },
  variants: {
    mode: {
      interactive: {},
      static: {
        root: {
          cursor: "initial",
          _hover: {
            borderBlockStartColor: "border",
            borderBlockEndColor: "border",
            borderInlineStartColor: "border",
            borderInlineEndColor: "border",
          },
        },
      },
      empty: {
        root: {
          backgroundColor: "bg.surface.strong",
          borderBlockStartColor: "[transparent]",
          borderBlockEndColor: "[transparent]",
          borderInlineStartColor: "[transparent]",
          borderInlineEndColor: "[transparent]",
          cursor: "initial",
          _hover: {
            borderBlockStartColor: "[transparent]",
            borderBlockEndColor: "[transparent]",
            borderInlineStartColor: "[transparent]",
            borderInlineEndColor: "[transparent]",
          },
        },
        // Empty days have no opponent/heading content — strip the topLine
        // border so the chip floats inside the muted tile background.
        topLine: {
          borderBlockStartColor: "[transparent]",
          borderBlockEndColor: "[transparent]",
          borderInlineStartColor: "[transparent]",
          borderInlineEndColor: "[transparent]",
        },
      },
    },
    primaryColorState: {
      applied: {
        topLine: {
          borderBlockStartColor: "[transparent]",
          borderBlockEndColor: "[transparent]",
          borderInlineStartColor: "[transparent]",
          borderInlineEndColor: "[transparent]",
          color: "text.onFill",
        },
      },
      none: {
        topLine: {
          borderBlockStartColor: "border",
          borderBlockEndColor: "border",
          borderInlineStartColor: "border",
          borderInlineEndColor: "border",
          color: "text",
        },
      },
    },
    minHeight: {
      clamped: {
        root: { minHeight: "[184px]" },
      },
      auto: {},
    },
    todayHighlight: {
      today: {},
      default: {},
    },
    dayOfMonthState: {
      normal: {},
      tba: {
        dayOfMonth: {
          width: "[calc(({spacing.primitives.25} * 2) + ({lineHeights.primitives.500} * 1.5))]",
        },
      },
    },
    headingBorder: {
      borderless: {
        topLine: {
          borderBlockStartColor: "[transparent]",
          borderBlockEndColor: "[transparent]",
          borderInlineStartColor: "[transparent]",
          borderInlineEndColor: "[transparent]",
        },
      },
      bordered: {},
    },
  },
  compoundVariants: [
    // Interactive tile without a primary color — hover uses border.stronger instead
    // of the primary-color var (which would resolve to transparent).
    {
      mode: "interactive",
      primaryColorState: "none",
      css: {
        root: {
          _hover: {
            borderBlockStartColor: "border.stronger",
            borderBlockEndColor: "border.stronger",
            borderInlineStartColor: "border.stronger",
            borderInlineEndColor: "border.stronger",
          },
        },
      },
    },
    // Empty days default to `primaryColorState: "none"`, which would otherwise
    // re-apply a `border` colour to the topLine (declared after `mode`, so it
    // wins the cascade). Re-assert transparent borders for the empty mode so
    // the day-of-month chip floats inside the muted tile background without a
    // divider line.
    {
      mode: "empty",
      primaryColorState: "none",
      css: {
        topLine: {
          borderBlockStartColor: "[transparent]",
          borderBlockEndColor: "[transparent]",
          borderInlineStartColor: "[transparent]",
          borderInlineEndColor: "[transparent]",
        },
      },
    },
    // Today highlight on a primary-coloured top line — translucent white pill.
    {
      todayHighlight: "today",
      primaryColorState: "applied",
      dayOfMonthState: "normal",
      css: {
        dayOfMonth: { backgroundColor: "[rgba(255, 255, 255, 0.2)]" },
      },
    },
    // Today highlight on the colour-fallback top line — translucent black pill
    // so it still contrasts with the default top-line background.
    {
      todayHighlight: "today",
      primaryColorState: "none",
      dayOfMonthState: "normal",
      css: {
        dayOfMonth: { backgroundColor: "[rgba(0, 0, 0, 0.1)]" },
      },
    },
  ],
  defaultVariants: {
    mode: "interactive",
    primaryColorState: "none",
    minHeight: "auto",
    todayHighlight: "default",
    dayOfMonthState: "normal",
    headingBorder: "bordered",
  },
});

/**
 * Slot recipe for one half-tile portion (used inside a multi-event day card).
 * Replaces `portionContainer` + `portionContainerColorFallback` and
 * `portionTopLine` + `portionTopLineColorFallback`.
 *
 * Variants:
 * - `primaryColor` — whether `--component-primary-color` is set on the
 *   portion (`"applied"` | `"default"`). Drives top-line border colour and
 *   the hover treatment (border collapses to `border.stronger` when no
 *   primary colour is provided so the hover state is still visible).
 */
export const calendarTilePortion = sva({
  slots: ["root", "topLine"],
  className: "calendarTilePortion",
  base: {
    root: {
      display: "flex",
      height: "full",
      minHeight: "[68px]",
      flexDirection: "column",
      flexWrap: "nowrap",
      alignItems: "center",
      justifyContent: "center",
      paddingBlock: "primitives.0",
      paddingInline: "primitives.0",
      columnGap: "primitives.0",
      rowGap: "primitives.0",
      borderBlockStartStyle: "solid",
      borderBlockEndStyle: "solid",
      borderInlineStartStyle: "solid",
      borderInlineEndStyle: "solid",
      borderBlockStartWidth: "primitives.25",
      borderBlockEndWidth: "primitives.25",
      borderInlineStartWidth: "primitives.25",
      borderInlineEndWidth: "primitives.25",
      borderBlockStartColor: "border",
      borderBlockEndColor: "border",
      borderInlineStartColor: "border",
      borderInlineEndColor: "border",
      borderStartStartRadius: "element.default",
      borderStartEndRadius: "element.default",
      borderEndStartRadius: "element.default",
      borderEndEndRadius: "element.default",
      textAlign: "start",
      overflowX: "hidden",
      overflowY: "hidden",
      cursor: "pointer",
      transitionProperty: "[all]",
      transitionDuration: "[150ms]",
      transitionTimingFunction: "interaction.press",
      _hover: {
        backgroundColor: "bg.surface",
        borderBlockStartColor: "var(--component-primary-color)",
        borderBlockEndColor: "var(--component-primary-color)",
        borderInlineStartColor: "var(--component-primary-color)",
        borderInlineEndColor: "var(--component-primary-color)",
      },
      _active: { backgroundColor: "bg.surface" },
    },
    topLine: {
      width: "full",
      height: "[8px]",
      flexGrow: 0,
      flexShrink: 0,
      backgroundColor: "var(--component-primary-color)",
      borderBlockEndWidth: "primitives.25",
      borderBlockStartStyle: "solid",
      borderBlockEndStyle: "solid",
      borderInlineStartStyle: "solid",
      borderInlineEndStyle: "solid",
      borderBlockStartColor: "[transparent]",
      borderBlockEndColor: "[transparent]",
      borderInlineStartColor: "[transparent]",
      borderInlineEndColor: "[transparent]",
    },
  },
  variants: {
    primaryColor: {
      applied: {},
      default: {
        root: {
          _hover: {
            borderBlockStartColor: "border.stronger",
            borderBlockEndColor: "border.stronger",
            borderInlineStartColor: "border.stronger",
            borderInlineEndColor: "border.stronger",
          },
        },
        topLine: {
          borderBlockStartColor: "border",
          borderBlockEndColor: "border",
          borderInlineStartColor: "border",
          borderInlineEndColor: "border",
        },
      },
    },
  },
  defaultVariants: {
    primaryColor: "default",
  },
});
